// import { Constructor } from '@angular/cdk/table';
import { CollectionHelper, CompareHelper, ObjectHelper } from '@barthauer/abc/core';
import { ParameterDefinition } from '@barthauer/abc/queries/parameters/core';
import { Command, NavigationDocument, NavigationGroup, NavigationNode, NavigationNodeBase } from './navigation.model';
type Constructor<T = {}> = new (...args: any[]) => T;
export class NavigationViewModel {
  public navigationDocument: NavigationDocument;
  private _layoutsByObjectType: Map<string, NavigationLayoutViewModel[]>;
  private _layoutsByLayoutId: Map<string, NavigationLayoutViewModel[]>;
  private _layoutsByFullName: Map<string, NavigationLayoutViewModel[]>;
  private _groupsByFullName: Map<string, NavigationGroupViewModel[]>;
  private _parameterDefinitions: ParameterDefinition[];
  private _groups: NavigationGroupViewModel[];

  public get groups(): NavigationGroupViewModel[] {
    if (!this._groups) {
      this._groups = [];
      for (const g of this.navigationDocument.Groups) {
        this._groups.push(new NavigationGroupViewModel(this, null, g));
      }
    }
    return this._groups;
  }

  private appendLayouts<TKey, TItem extends NavigationNodeBaseViewModel>(
    layouts: Map<TKey, TItem[]>,
    nodes: NavigationNodeBaseViewModel[],
    getLayoutKey: (layout: TItem) => TKey,
    type: Constructor<TItem>,
  ) {
    if (Array.isArray(nodes)) {
      for (const n of nodes) {
        if (ObjectHelper.isOfType(n, type)) {
          const i = <TItem>n;
          const key = getLayoutKey(i);
          if (key) {
            let arr = layouts.get(key);
            if (!arr) {
              arr = [];
              layouts.set(key, arr);
            }
            arr.push(i);
          }
        }
        if (n instanceof NavigationGroupViewModel) {
          this.appendLayouts(layouts, n.nodes, getLayoutKey, type);
        }
      }
    }
  }

  private prepareKey(key: string): string {
    return NavigationNodeBaseViewModel.prepareName(key?.toLowerCase());
  }

  private findNodesCore<T extends NavigationNodeBaseViewModel>(
    map: Map<string, T[]>,
    key: string,
    onlyVisible: boolean,
  ): T[] {
    let result = map.get(this.prepareKey(key));
    if (result?.length && onlyVisible) {
      result = result.filter((_) => _.mainGroup.data.VisibleIndex >= 0);
    }
    return result;
  }

  private get layoutsByObjectType(): Map<string, NavigationLayoutViewModel[]> {
    if (!this._layoutsByObjectType) {
      const layouts = new Map<string, NavigationLayoutViewModel[]>();
      this.appendLayouts(layouts, this.groups, (_) => this.prepareKey(_?.objectType), NavigationLayoutViewModel);
      this._layoutsByObjectType = layouts;
    }
    return this._layoutsByObjectType;
  }

  public findLayoutsByObjectType(objectType: string): NavigationLayoutViewModel[] {
    let arr = this.findNodesCore(this.layoutsByObjectType, objectType, true);
    if (!arr?.find((_) => !_.nodeFilter)) {
      const arr2 = this.findNodesCore(this.layoutsByObjectType, objectType, false);
      if (arr2?.find((_) => !_.nodeFilter)) {
        arr = arr2;
      }
    }
    return this.sortLayouts(arr);
  }

  public get layoutsByLayoutId(): Map<string, NavigationLayoutViewModel[]> {
    if (!this._layoutsByLayoutId) {
      const layouts = new Map<string, NavigationLayoutViewModel[]>();
      this.appendLayouts(layouts, this.groups, (_) => this.prepareKey(_?.layoutName), NavigationLayoutViewModel);
      this._layoutsByLayoutId = layouts;
    }
    return this._layoutsByLayoutId;
  }

  private findLayoutsByLayoutId(layoutId: string, onlyVisible: boolean): NavigationLayoutViewModel[] {
    return this.findNodesCore(this.layoutsByLayoutId, layoutId, onlyVisible);
  }

  private get layoutsByFullName(): Map<string, NavigationLayoutViewModel[]> {
    if (!this._layoutsByFullName) {
      const layouts = new Map<string, NavigationLayoutViewModel[]>();
      this.appendLayouts(layouts, this.groups, (_) => this.prepareKey(_?.fullName), NavigationLayoutViewModel);
      this._layoutsByFullName = layouts;
    }
    return this._layoutsByFullName;
  }

  public findLayoutByFullName(fullName: string, onlyVisible: boolean): NavigationLayoutViewModel {
    const arr = this.findNodesCore(this.layoutsByFullName, fullName, onlyVisible);
    if (arr?.length) {
      return arr[0];
    }
  }

  private get groupsByFullName(): Map<string, NavigationGroupViewModel[]> {
    if (!this._groupsByFullName) {
      const groups = new Map<string, NavigationGroupViewModel[]>();
      this.appendLayouts(groups, this.groups, (_) => this.prepareKey(_?.fullName), NavigationGroupViewModel);
      this._groupsByFullName = groups;
    }
    return this._groupsByFullName;
  }

  public findGroupByFullName(fullName: string, onlyVisible: boolean): NavigationGroupViewModel {
    const arr = this.findNodesCore(this.groupsByFullName, fullName, onlyVisible);
    if (arr?.length) {
      return arr[0];
    }
  }

  public findBestLayoutByLayoutId(layoutId: string): NavigationLayoutViewModel {
    const lyVisible = this.findBestLayoutByLayoutIdCore(layoutId, true);
    if (!lyVisible || lyVisible.nodeFilter) {
      const lyInvisible = this.findBestLayoutByLayoutIdCore(layoutId, false);
      if (!lyVisible || (lyInvisible && !lyInvisible.nodeFilter)) {
        return lyInvisible;
      }
    }
    return lyVisible;
  }

  private findBestLayoutByLayoutIdCore(layoutId: string, onlyVisible: boolean): NavigationLayoutViewModel {
    let navLayouts = this.findLayoutsByLayoutId(layoutId, onlyVisible);
    if (navLayouts?.length) {
      return this.sortLayouts(navLayouts)[0];
    }
  }

  private sortLayouts(navLayouts: NavigationLayoutViewModel[]): NavigationLayoutViewModel[] {
    return navLayouts?.sort((l1, l2) => {
      let result = CompareHelper.compare(l1.nodeFilter?.length ?? 0, l2.nodeFilter?.length ?? 0);
      if (result === 0) {
        result = CompareHelper.compare(l1.fullName.length, l2.fullName.length);
      }
      return result;
    });
  }

  get hasLayouts(): boolean {
    return this.layoutsByLayoutId.size > 0;
  }

  get parameterDefinitions() {
    if (!this._parameterDefinitions) {
      this._parameterDefinitions = [];
      if (Array.isArray(this.navigationDocument.ParameterDefinitions)) {
        for (const pd of this.navigationDocument.ParameterDefinitions) {
          this._parameterDefinitions.push(pd);
        }
      }
    }
    return this._parameterDefinitions;
  }

  constructor(data: NavigationDocument) {
    this.navigationDocument = data;
  }
}

export class NavigationNodeBaseViewModel {
  public static prepareName(name: string): string {
    return name
      ?.replace(/Ä/g, 'Ae')
      .replace(/ä/g, 'ae')
      .replace(/Ö/g, 'Oe')
      .replace(/ö/g, 'oe')
      .replace(/Ü/g, 'Ue')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/&/g, '+');
  }
  get name(): string {
    return NavigationNodeBaseViewModel.prepareName(this._node.Name);
  }
  get caption(): string {
    return this._node.Caption;
  }
  get fullName(): string {
    if (this.group) {
      return this.group.fullName + '\\' + this.name;
    } else {
      return this.name;
    }
  }
  get mainGroup(): NavigationGroupViewModel {
    let g = this instanceof NavigationGroupViewModel ? this : this.group;
    while (g?.group) {
      g = g.group;
    }
    return g;
  }

  constructor(
    public readonly document: NavigationViewModel,
    public readonly group: NavigationGroupViewModel,
    protected _node: NavigationNodeBase,
  ) {}
}

export class NavigationGroupViewModel extends NavigationNodeBaseViewModel {
  private _nodes: NavigationNodeBaseViewModel[];

  get markerTag(): string {
    return this._node.MarkerTag;
  }

  public get nodes(): NavigationNodeBaseViewModel[] {
    if (!this._nodes) {
      this._nodes = [];
      if (Array.isArray(this.data.Nodes)) {
        for (const node of this.data.Nodes) {
          if (node) {
            if (node instanceof NavigationNode || node.Command?.Identifier) {
              this._nodes.push(new NavigationLayoutViewModel(this.document, this, <NavigationNode>node));
            } else if (node instanceof NavigationGroup) {
              this._nodes.push(new NavigationGroupViewModel(this.document, this, node));
            }
          }
        }
      }
    }
    return this._nodes;
  }

  get data(): NavigationGroup {
    return <NavigationGroup>this._node;
  }

  get badgeValue(): number {
    return NavigationGroupViewModel.sumBadgeValues([this]);
  }

  public static sumBadgeValues(nodes: NavigationNodeBaseViewModel[]): number {
    let sum = 0;
    if (nodes) {
      for (const n of nodes) {
        if (n instanceof NavigationLayoutViewModel) {
          sum += n.badgeValue;
        } else if (n instanceof NavigationGroupViewModel) {
          sum += this.sumBadgeValues(n.nodes);
        }
      }
    }
    return sum;
  }

  constructor(document: NavigationViewModel, parent: NavigationGroupViewModel, group: NavigationGroup) {
    super(document, parent, group);
  }
}

export class NavigationLayoutViewModel extends NavigationNodeBaseViewModel {
  get nodeFilter(): string {
    return this.data.Filter;
  }
  get layoutName(): string {
    return this.data.Layout?.Name;
  }
  get layoutCaption(): string {
    return this.data.Layout?.Caption;
  }
  get objectType(): string {
    return this.data.Layout?.ObjectType;
  }
  get data(): NavigationNode {
    return <NavigationNode>this._node;
  }
  get command(): Command {
    return this._node.Command;
  }
  private _badgeValue: number;
  get badgeValue(): number {
    return this._badgeValue ?? 0;
  }
  private _badgeInfos: Map<string, string[]>;
  get badgeInfos(): Map<string, string[]> {
    return this._badgeInfos;
  }
  set badgeInfos(value: Map<string, string[]>) {
    this._badgeInfos = value;
    this.refreshBadgeValue();
  }

  public refreshBadgeValue(): void {
    if (this.badgeInfos) {
      this._badgeValue = CollectionHelper.distinctValues(
        CollectionHelper.flatten(Array.from(this.badgeInfos.values())),
      ).length;
    } else {
      this._badgeValue = undefined;
    }
  }

  constructor(document: NavigationViewModel, parent: NavigationGroupViewModel, _node: NavigationNode) {
    super(document, parent, _node);
  }
}
